import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye, EyeOff, Pin, PinOff, Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  DeleteConfirmDialog,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef,
  Badge
} from '@bliks-io/ui';
import type { PartnerNewsAdmin } from '@bliks-io/contracts';
import { useAdminPartnerNews } from '@/pages/admin/api/queries/usePartnerNews';
import {
  useDeletePartnerNews,
  useUpdatePartnerNews
} from '@/pages/admin/api/mutations/usePartnerNews';
import { formatDate } from '@/lib/formatters';
import { PublishStatusBadge } from '@/pages/admin/partner-content/components/PublishStatusBadge';
import { usePublishFilter } from '@/pages/admin/partner-content/usePublishFilter';
import { NewsDialog } from './NewsDialog';

export function NewsTab() {
  const { t } = useTranslation();
  const { data: news = [], isLoading, isError } = useAdminPartnerNews();
  const updateNews = useUpdatePartnerNews();
  const deleteNews = useDeletePartnerNews();
  const { matches, quickFilter } = usePublishFilter();

  // Each opening gets its own key, so the dialog never shows a previous draft.
  const [editor, setEditor] = useState<{ session: number; news: PartnerNewsAdmin | null }>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState<PartnerNewsAdmin | null>(null);

  const openEditor = (entry: PartnerNewsAdmin | null) => {
    setEditor((current) => ({ session: (current?.session ?? 0) + 1, news: entry }));
    setDialogOpen(true);
  };
  const patch = (entry: PartnerNewsAdmin, data: { published?: boolean; pinned?: boolean }) =>
    updateNews.mutate({ id: entry._id, data }, { onError: () => toast.error(t('common.Error')) });

  const visible = useMemo(() => news.filter((entry) => matches(entry.published)), [news, matches]);

  const columns: DataTableColumnDef<PartnerNewsAdmin>[] = [
    {
      id: 'title',
      accessorFn: (entry) => entry.title.de,
      header: createColumnHeader(t('partnerContent.columns.title')),
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => openEditor(row.original)}
          className="flex min-w-0 items-center gap-2 text-left hover:underline"
        >
          {row.original.pinned && (
            <Pin
              className="h-4 w-4 shrink-0 text-muted-foreground"
              aria-label={t('partner.home.news.pinned')}
            />
          )}
          <span className="truncate font-medium">{row.original.title.de}</span>
          {row.original.audience === 'sales' && (
            <Badge variant="outline" size="sm" className="shrink-0">
              {t('partnerContent.news.audienceSales')}
            </Badge>
          )}
        </button>
      )
    },
    {
      id: 'eyebrow',
      accessorFn: (entry) => entry.eyebrow?.de ?? '',
      header: createColumnHeader(t('partnerContent.news.eyebrow')),
      cell: ({ getValue }) => (
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {getValue<string>() || '—'}
        </span>
      )
    },
    {
      id: 'status',
      meta: { fitContent: true },
      header: createColumnHeader(t('partnerContent.columns.status')),
      cell: ({ row }) => <PublishStatusBadge published={row.original.published} />
    },
    {
      id: 'publishedAt',
      meta: { fitContent: true },
      header: createColumnHeader(t('partnerContent.columns.publishedAt')),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {row.original.publishedAt ? formatDate(row.original.publishedAt) : '—'}
        </span>
      )
    },
    createActionsColumn<PartnerNewsAdmin>({
      onEdit: (entry) => openEditor(entry),
      onDelete: (entry) => setDeleting(entry),
      getCustomActions: (entry) => [
        entry.published
          ? {
              label: t('partnerContent.actions.unpublish'),
              icon: <EyeOff className="h-4 w-4" />,
              onClick: () => patch(entry, { published: false })
            }
          : {
              label: t('partnerContent.actions.publish'),
              icon: <Eye className="h-4 w-4" />,
              onClick: () => patch(entry, { published: true })
            },
        entry.pinned
          ? {
              label: t('partnerContent.news.unpin'),
              icon: <PinOff className="h-4 w-4" />,
              onClick: () => patch(entry, { pinned: false })
            }
          : {
              label: t('partnerContent.news.pin'),
              icon: <Pin className="h-4 w-4" />,
              onClick: () => patch(entry, { pinned: true })
            }
      ]
    })
  ];

  if (isError) return <p className="text-destructive">{t('partnerContent.news.loadError')}</p>;

  return (
    <>
      <DataTable
        columns={columns}
        data={visible}
        isLoading={isLoading}
        enableSorting={false}
        enableGlobalFilter
        emptyMessage={t(
          news.length === 0 ? 'partnerContent.news.empty' : 'partnerContent.emptyFiltered'
        )}
        quickFilters={[quickFilter]}
        toolbarContent={
          <Button onClick={() => openEditor(null)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('partnerContent.news.add')}
          </Button>
        }
      />

      {editor && (
        <NewsDialog
          key={editor.session}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          news={editor.news}
        />
      )}

      <DeleteConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteNews.mutate(deleting._id, {
            onSuccess: () => setDeleting(null),
            onError: () => toast.error(t('common.Error'))
          });
        }}
        isDeleting={deleteNews.isPending}
        entityPhrase={t('partnerContent.news.deleteEntityPhrase')}
      />
    </>
  );
}
